<template>
  <div
    ref="formItemRef"
    :class="formItemClasses"
    :role="isGroup ? 'group' : undefined"
    :aria-labelledby="isGroup ? labelId : undefined"
    data-component-name="FormItem"
    data-event-container="true"
    :data-component-label="props.label || '表单项'"
  >
    <form-label-wrap
      v-if="formContext?.labelPosition !== 'hidden'"
      :is-auto-width="labelStyle.width === 'auto'"
      :update-all="formContext?.labelWidth === 'auto'"
    >
      <jd-tooltip v-if="hasTooltipProps" v-bind="resolvedTooltipProps">
        <component
          :is="labelFor ? 'label' : 'div'"
          v-if="hasLabel && !hasLabelSuffix"
          :id="labelId"
          :for="props.labelAssociate ? labelFor : undefined"
          :class="[
            ns.e('label'),
            ns.e(`label__${formContext?.asteriskPosition}`),
            { [ns.is('adapt-label')]: isAdaptLabelHeight },
            ns.is('tooltips', hasTooltipProps),
          ]"
          :style="labelStyle"
        >
          <slot name="label" :label="currentLabel">
            <span
              :class="[ns.e('label-text'), ns.is('tooltips', hasTooltipProps)]"
            >
              {{ currentLabel }}
            </span>
          </slot>
        </component>
        <template v-else>
          <component
            :is="labelFor ? 'label' : 'div'"
            v-if="hasLabel"
            :id="labelId"
            :for="props.labelAssociate ? labelFor : undefined"
            :class="[
              ns.e('label'),
              ns.is('label-flex', formContext?.labelPosition === 'top'),
              { [ns.is('adapt-label')]: isAdaptLabelHeight },
              ns.is('tooltips', hasTooltipProps),
            ]"
            :style="labelStyle"
          >
            <slot name="label" :label="currentLabel">
              <slot name="labelPrefix" />
              <label
                :class="[
                  ns.e('label'),
                  ns.is('nopadding'),
                  ns.e(`label__${formContext?.asteriskPosition}`),
                ]"
              >
                <span
                  :class="[
                    ns.e('label-text'),
                    ns.is('tooltips', hasTooltipProps),
                  ]"
                >
                  {{ currentLabel }}
                </span>
              </label>
            </slot>
            <slot name="labelSuffix" />
          </component>
        </template>
        <template v-if="isTooltipContentVNode" #content>
          <component :is="resolvedTooltipContent" />
        </template>
      </jd-tooltip>
      <template v-else>
        <component
          :is="labelFor ? 'label' : 'div'"
          v-if="hasLabel && !hasLabelSuffix"
          :id="labelId"
          :for="props.labelAssociate ? labelFor : undefined"
          :class="[
            ns.e('label'),
            ns.e(`label__${formContext?.asteriskPosition}`),
            { [ns.is('adapt-label')]: isAdaptLabelHeight },
          ]"
          :style="labelStyle"
        >
          <slot name="label" :label="currentLabel">
            <span :class="[ns.e('label-text')]">
              {{ currentLabel }}
            </span>
          </slot>
        </component>
        <template v-else>
          <component
            :is="labelFor ? 'label' : 'div'"
            v-if="hasLabel"
            :id="labelId"
            :for="props.labelAssociate ? labelFor : undefined"
            :class="[
              ns.e('label'),
              ns.is('label-flex', formContext?.labelPosition === 'top'),
              { [ns.is('adapt-label')]: isAdaptLabelHeight },
            ]"
            :style="labelStyle"
          >
            <slot name="label" :label="currentLabel">
              <slot name="labelPrefix" />
              <label
                :class="[
                  ns.e('label'),
                  ns.is('nopadding'),
                  ns.e(`label__${formContext?.asteriskPosition}`),
                ]"
              >
                <span :class="[ns.e('label-text')]">
                  {{ currentLabel }}
                </span>
              </label>
            </slot>
            <slot name="labelSuffix" />
          </component>
        </template>
      </template>
    </form-label-wrap>

    <jd-tooltip
      v-if="formContext?.labelPosition === 'hidden' && _showLabelTooltips"
      effect="light"
      :offset="4"
      :show-arrow="false"
      :content="currentLabel"
      placement="top-start"
    >
      <div :class="ns.e('content')" :style="contentStyle">
        <slot />
        <transition-group :name="`${ns.namespace.value}-zoom-in-top`">
          <slot v-if="shouldShowError" name="error" :error="validateMessage">
            <div :class="validateClasses">
              {{ validateMessage }}
            </div>
          </slot>
        </transition-group>
        <slot v-if="showHelp" name="help">
          <div :class="ns.e('help')">{{ help }}</div>
        </slot>
      </div>
    </jd-tooltip>
    <div v-else :class="ns.e('content')" :style="contentStyle">
      <slot />
      <transition-group :name="`${ns.namespace.value}-zoom-in-top`">
        <slot v-if="shouldShowError" name="error" :error="validateMessage">
          <div :class="validateClasses">
            {{ validateMessage }}
          </div>
        </slot>
      </transition-group>
      <slot v-if="showHelp" name="help">
        <div :class="ns.e('help')">{{ help }}</div>
      </slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  reactive,
  ref,
  toRefs,
  useSlots,
  watch,
  onUpdated,
} from 'vue'
import AsyncValidator from 'async-validator'
import { clone } from 'lodash-unified'
import { refDebounced } from '@vueuse/core'
import {
  addUnit,
  ensureArray,
  getProp,
  isBoolean,
  isFunction,
  isString,
} from '@jdesign-vue/utils'
import { useId, useNamespace } from '@jdesign-vue/hooks'
import { useFormSize } from './hooks'
import { formItemProps } from './form-item'
import FormLabelWrap from './form-label-wrap'
import { formContextKey, formItemContextKey } from './constants'

import type { CSSProperties } from 'vue'
import type { RuleItem } from 'async-validator'
import type { Arrayable } from '@jdesign-vue/utils'
import type {
  FormItemContext,
  FormItemRule,
  FormValidateFailure,
} from './types'
import type { FormItemValidateState } from './form-item'

defineOptions({
  name: 'JdFormItem',
})
const props = defineProps(formItemProps)
const slots = useSlots()

const formContext = inject(formContextKey, undefined)
const parentFormItemContext = inject(formItemContextKey, undefined)
const unifyLabelHeight = inject('unifyLabelHeight', false)

const _size = useFormSize(undefined, { formItem: false })
const ns = useNamespace('form-item')

const labelId = useId().value
const inputIds = ref<string[]>([])

const validateState = ref<FormItemValidateState>('')
const validateStateDebounced = refDebounced(validateState, 100)
const validateMessage = ref('')
const formItemRef = ref<HTMLDivElement>()
// special inline value.
let initialValue: any = undefined
let isResettingField = false
const fixAdaptLabelHeightWhiteList = ['checkbox', 'radio', 'switch']

const isAdaptLabelHeight = computed(
  () =>
    formContext?.contentAdaptiveHeight &&
    fixAdaptLabelHeightWhiteList.includes(props.contentType || '')
)

const labelStyle = computed<CSSProperties>(() => {
  if (formContext?.labelPosition === 'top') {
    return {}
  }

  const labelWidth = addUnit(props.labelWidth || formContext?.labelWidth || '')
  const labelHeight = addUnit(props.labelHeight)

  const style: CSSProperties = {}

  if (labelWidth) {
    style.width = labelWidth
  }
  if (labelHeight) {
    style.height = labelHeight
  }
  return style
})

const contentStyle = computed<CSSProperties>(() => {
  if (formContext?.labelPosition === 'top' || formContext?.inline) {
    return {}
  }
  if (!props.label && !props.labelWidth && isNested) {
    return {}
  }
  const labelWidth = addUnit(props.labelWidth || formContext?.labelWidth || '')
  if (!props.label && !slots.label) {
    return { marginLeft: labelWidth }
  }
  return {}
})

// 是否存在label后缀或前缀
const hasLabelSuffix = computed(() => {
  return slots.labelSuffix || slots.labelPrefix
})

const formItemClasses = computed(() => [
  ns.b(),
  ns.m(_size.value),
  ns.is('error', validateState.value === 'error'),
  ns.is('validating', validateState.value === 'validating'),
  ns.is('success', validateState.value === 'success'),
  ns.is('required', isRequired.value || props.required),
  ns.is('no-asterisk', formContext?.hideRequiredAsterisk),
  formContext?.requireAsteriskPosition === 'right'
    ? 'asterisk-right'
    : 'asterisk-left',
  { [ns.m('feedback')]: formContext?.statusIcon },
])

const _inlineMessage = computed(() =>
  isBoolean(props.inlineMessage)
    ? props.inlineMessage
    : formContext?.inlineMessage || false
)

const _staticMessage = computed(() =>
  isBoolean(props.staticMessage)
    ? props.staticMessage
    : formContext?.staticMessage || false
)

// 是否展示labelToolTips
const _showLabelTooltips = computed(() =>
  isBoolean(props.showLabelTooltips)
    ? props.showLabelTooltips
    : formContext?.showLabelTooltips || false
)

const validateClasses = computed(() => [
  ns.e('error'),
  { [ns.em('error', 'inline')]: _inlineMessage.value },
  { [ns.em('error', 'static')]: _staticMessage.value },
])

const propString = computed(() => {
  if (!props.prop) return ''
  return isString(props.prop) ? props.prop : props.prop.join('.')
})

const hasLabel = computed<boolean>(() => {
  return !!(props.label || slots.label)
})

const labelFor = computed<string | undefined>(() => {
  return props.for || inputIds.value.length === 1
    ? inputIds.value[0]
    : undefined
})

const isGroup = computed<boolean>(() => {
  return !labelFor.value && hasLabel.value
})

// 兼容 tooltips 属性：tooltipContent 优先，tooltips 作为回退
const effectiveTooltipContent = computed(
  () => props.tooltipContent ?? props.tooltips
)

const hasTooltipProps = computed<boolean>(() => {
  return (
    (!!props.tooltipProps && Object.keys(props.tooltipProps).length > 0) ||
    !!effectiveTooltipContent.value
  )
})

// tooltipContent 是否为 VNode 或返回 VNode 的函数
const isTooltipContentVNode = computed<boolean>(
  () =>
    effectiveTooltipContent.value != null &&
    typeof effectiveTooltipContent.value !== 'string'
)

// 解析 tooltipContent：如果是函数则调用，否则直接返回
const resolvedTooltipContent = computed(() => {
  if (typeof effectiveTooltipContent.value === 'function') {
    return effectiveTooltipContent.value()
  }
  return effectiveTooltipContent.value
})

// 合并后的 tooltip props：处理 content 优先级
const resolvedTooltipProps = computed(() => {
  if (!props.tooltipProps) {
    // tooltipProps 未传，tooltipContent 为 string 时作为 content
    if (typeof effectiveTooltipContent.value === 'string') {
      return { content: effectiveTooltipContent.value }
    }
    return {}
  }
  // tooltipContent 为 VNode 时，移除 content 避免冲突（由 #content 插槽渲染）
  if (isTooltipContentVNode.value) {
    const { content: _, ...rest } = props.tooltipProps
    return rest
  }
  // tooltipContent 为 string 时，覆盖 tooltipProps.content
  if (typeof effectiveTooltipContent.value === 'string') {
    return { ...props.tooltipProps, content: effectiveTooltipContent.value }
  }
  return props.tooltipProps
})

const isNested = !!parentFormItemContext

const fieldValue = computed(() => {
  const model = formContext?.model
  if (!model || !props.prop) {
    return
  }
  return getProp(model, props.prop).value
})

const normalizedRules = computed(() => {
  const { required } = props

  const rules: FormItemRule[] = []

  if (props.rules) {
    rules.push(...ensureArray(props.rules))
  }

  const formRules = formContext?.rules
  if (formRules && props.prop) {
    const _rules = getProp<Arrayable<FormItemRule> | undefined>(
      formRules,
      props.prop
    ).value
    if (_rules) {
      rules.push(...ensureArray(_rules))
    }
  }

  if (required !== undefined) {
    const requiredRules = rules
      .map((rule, i) => [rule, i] as const)
      .filter(([rule]) => Object.keys(rule).includes('required'))

    if (requiredRules.length > 0) {
      for (const [rule, i] of requiredRules) {
        if (rule.required === required) continue
        rules[i] = { ...rule, required }
      }
    } else {
      rules.push({ required })
    }
  }

  return rules
})

const validateEnabled = computed(() => normalizedRules.value.length > 0)

const showHelp = computed(() => !!props.help || !!slots.help)

const getFilteredRule = (trigger: string) => {
  const rules = normalizedRules.value
  return (
    rules
      .filter((rule) => {
        if (!rule.trigger || !trigger) return true
        if (Array.isArray(rule.trigger)) {
          return rule.trigger.includes(trigger)
        } else {
          return rule.trigger === trigger
        }
      })
      // exclude trigger
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .map(({ trigger, ...rule }): RuleItem => rule)
  )
}

const isRequired = computed(() =>
  normalizedRules.value.some((rule) => rule.required)
)

const shouldShowError = computed(
  () =>
    validateStateDebounced.value === 'error' &&
    props.showMessage &&
    (formContext?.showMessage ?? true)
)

const currentLabel = computed(
  () => `${props.label || ''}${formContext?.labelSuffix || ''}`
)

const setValidationState = (state: FormItemValidateState) => {
  validateState.value = state
}

const onValidationFailed = (error: FormValidateFailure) => {
  const { errors, fields } = error
  if (!errors || !fields) {
    console.error(error)
  }

  setValidationState('error')
  validateMessage.value = errors
    ? errors?.[0]?.message ?? `${props.prop} is required`
    : ''

  formContext?.emit('validate', props.prop!, false, validateMessage.value)
}

const onValidationSucceeded = () => {
  setValidationState('success')
  formContext?.emit('validate', props.prop!, true, '')
}

const doValidate = async (rules: RuleItem[]): Promise<true> => {
  const modelName = propString.value
  const validator = new AsyncValidator({
    [modelName]: rules,
  })
  return validator
    .validate({ [modelName]: fieldValue.value }, { firstFields: true })
    .then(() => {
      onValidationSucceeded()
      return true as const
    })
    .catch((err: FormValidateFailure) => {
      onValidationFailed(err as FormValidateFailure)
      return Promise.reject(err)
    })
}

const validate: FormItemContext['validate'] = async (trigger, callback) => {
  // skip validation if its resetting
  if (isResettingField || !props.prop) {
    return false
  }

  const hasCallback = isFunction(callback)
  if (!validateEnabled.value) {
    callback?.(false)
    return false
  }

  const rules = getFilteredRule(trigger)
  if (rules.length === 0) {
    callback?.(true)
    return true
  }

  setValidationState('validating')

  return doValidate(rules)
    .then(() => {
      callback?.(true)
      return true as const
    })
    .catch((err: FormValidateFailure) => {
      const { fields } = err
      callback?.(false, fields)
      return hasCallback ? false : Promise.reject(fields)
    })
}

const clearValidate: FormItemContext['clearValidate'] = () => {
  setValidationState('')
  validateMessage.value = ''
  isResettingField = false
}

const resetField: FormItemContext['resetField'] = async () => {
  const model = formContext?.model
  if (!model || !props.prop) return

  const computedValue = getProp(model, props.prop)

  // prevent validation from being triggered
  isResettingField = true

  computedValue.value = clone(initialValue)

  await nextTick()
  clearValidate()

  isResettingField = false
}

const addInputId: FormItemContext['addInputId'] = (id: string) => {
  if (!inputIds.value.includes(id)) {
    inputIds.value.push(id)
  }
}

const removeInputId: FormItemContext['removeInputId'] = (id: string) => {
  inputIds.value = inputIds.value.filter((listId) => listId !== id)
}

watch(
  () => props.error,
  (val) => {
    validateMessage.value = val || ''
    setValidationState(val ? 'error' : '')
  },
  { immediate: true }
)

watch(
  () => props.validateStatus,
  (val) => setValidationState(val || '')
)

const context: FormItemContext = reactive({
  ...toRefs(props),
  $el: formItemRef,
  size: _size,
  validateState,
  labelId,
  inputIds,
  isGroup,
  hasLabel,
  addInputId,
  removeInputId,
  resetField,
  clearValidate,
  validate,
})

provide(formItemContextKey, context)

onMounted(() => {
  if (props.prop) {
    formContext?.addField(context)
    initialValue = clone(fieldValue.value)
  }
})

onBeforeUnmount(() => {
  formContext?.removeField(context)
})

defineExpose({
  /**
   * @description Form item size.
   */
  size: _size,
  /**
   * @description Validation message.
   */
  validateMessage,
  /**
   * @description Validation state.
   */
  validateState,
  /**
   * @description Validate form item.
   */
  validate,
  /**
   * @description Remove validation status of the field.
   */
  clearValidate,
  /**
   * @description Reset current field and remove validation result.
   */
  resetField,
})

// 监听unifyLabelHeight变化，更新label样式
watch(
  () => unifyLabelHeight,
  (newVal) => {
    if (newVal && formItemRef.value) {
      nextTick(() => {
        // 等待父组件统一高度完成后，确保当前item的label高度一致
        const labelElement = formItemRef.value?.querySelector(
          `.${ns.e('label')}`
        ) as HTMLElement
        if (labelElement) {
          // 添加统一的class用于标识
          labelElement.classList.add('unified-height')
        }
      })
    } else if (formItemRef.value) {
      // 移除统一高度样式
      const labelElement = formItemRef.value?.querySelector(
        `.${ns.e('label')}`
      ) as HTMLElement
      if (labelElement) {
        labelElement.classList.remove('unified-height')
        labelElement.style.height = ''
      }
    }
  },
  { immediate: true }
)

// 组件更新时检查是否需要统一高度
onUpdated(() => {
  if (unifyLabelHeight && formItemRef.value) {
    nextTick(() => {
      const labelElement = formItemRef.value?.querySelector(
        `.${ns.e('label')}`
      ) as HTMLElement
      if (labelElement) {
        labelElement.classList.add('unified-height')
      }
    })
  }
})
</script>
